import { Observable } from 'rxjs';
import { QualityControl } from '../entities/quality-control.entity';
import { QcCommunicationHistoryItem } from '../entities/qc-communication-history.entity';
import { QcVerifications } from '../entities/quality-control-verification.entity';
import { QcAsset } from '../entities/quality-control-asset.entity';
import { QcDocument } from '../entities/quality-control-document.entity';
import { AssetUseType } from '../entities/asset-use-type.entity';
import { AssetRequiredField } from '../entities/asset-required-field.entity';
import { KoOption } from '../entities/ko-option.entity';
import { QualityControlOriginOption } from '../entities/quality-control-origin.entity';
import { QualityControlInterfaceData } from '../entities/quality-control-interface-data.entity';
import { QualityControlFinishResult } from '../entities/quality-control-finish-result.entity';
import { EditAssetItemDto } from '../../application/dtos/edit-assets.dto';
import { EditAppraisalValidationDto, EditAssetValidationDto } from '../../application/dtos/edit-validations.dto';
import { CreateOrderWithQualityControlDocumentDto } from 'app/modules/orders/application/dtos/create-order-with-quality-control.dto';

export interface QualityControlListPagedRequest {
  orderId: string;
  page: number;
  pageSize: number;
  orderField?: string;
  ascendingOrder: boolean;
  filter?: QualityControlListFilter;
}

export interface QualityControlListFilter {
  code?: string;
  status?: number;
  technicalUserIds?: string[];
  qualityControlResults?: number[];
  qualityControlStatus?: number[];
  createdAtFrom?: string;
  createdAtTo?: string;
  finishedAtFrom?: string;
  finishedAtTo?: string;
  portfolioIds?: string[];
  clientIds?: string[];
  orderCreatedAtFrom?: string;
  orderCreatedAtTo?: string;
  isCancelled?: boolean;
  appraisalCompany?: number;
  appraisalPurpose?: number;
  versionFrom?: number;
  versionTo?: number;
  propertyRegistryNumber?: string;
  idufir?: string;
  cadastralReference?: string;
  contractIds?: number[];
  communicationsRequireAction?: boolean | null;
  idBien?: string;
  idOrigen?: string;
}

export interface QualityControlGetListPagedRequest {
  page: number;
  pageSize: number;
  orderField?: string;
  ascendingOrder: boolean;
  filter?: QualityControlListFilter;
}

export interface QualityControlListPagedResponse {
  items: QualityControl[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
}

export interface QcDocumentUpload {
  file: File;
  documentType: number;
  documentOrigin: number;
  qualityControlAssetId?: string | null;
}

export interface QcDocumentListFilter {
  documentTypes?: number[];
  documentOrigin?: number[];
}

export interface QcDocumentListPagedRequest {
  qualityControlId: string;
  page: number;
  pageSize: number;
  orderField?: string;
  ascendingOrder: boolean;
  filter?: QcDocumentListFilter;
}

export interface OrderQcDocumentListPagedRequest {
  orderId: string;
  page: number;
  pageSize: number;
  orderField?: string;
  ascendingOrder: boolean;
  filter?: QcDocumentListFilter;
}

export interface QcDocumentListPagedResponse {
  items: QcDocument[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
}

export abstract class QualityControlRepositoryPort {
  abstract getListPaged(request: QualityControlGetListPagedRequest): Observable<QualityControlListPagedResponse>;
  abstract getListPagedByOrder(request: QualityControlListPagedRequest): Observable<QualityControlListPagedResponse>;
  abstract getDetails(id: string): Observable<QualityControl>;
  abstract create(orderId: string, appraisalCompany?: number, appraisalPurpose?: number, appraisalReportNumber?: string | null, appraisalIssueDate?: Date | null, appraiserName?: string | null, documents?: CreateOrderWithQualityControlDocumentDto[], origin?: number | null): Observable<string>;
  abstract update(qualityControlId: string, appraisalCompany?: number, appraisalPurpose?: number, appraisalReportNumber?: string | null, appraisalIssueDate?: Date | null, appraiserName?: string | null, origin?: number | null): Observable<void>;
  abstract getAssets(qualityControlId: string): Observable<QcAsset[]>;
  abstract editAssets(qualityControlId: string, assets: EditAssetItemDto[]): Observable<void>;
  abstract getValidations(qualityControlId: string): Observable<QcVerifications>;
  abstract editValidations(qualityControlId: string, appraisalValidation: EditAppraisalValidationDto, assetValidations: EditAssetValidationDto[]): Observable<void>;
  abstract finishValidation(qualityControlId: string): Observable<QualityControlFinishResult>;
  abstract startValidation(qualityControlId: string): Observable<void>;
  abstract assignTechnical(qualityControlId: string, userId: string): Observable<void>;
  abstract getQcDocuments(request: QcDocumentListPagedRequest): Observable<QcDocumentListPagedResponse>;
  abstract getQcDocumentsByOrder(request: OrderQcDocumentListPagedRequest): Observable<QcDocumentListPagedResponse>;
  abstract uploadQcDocuments(qualityControlId: string, documents: QcDocumentUpload[]): Observable<void>;
  abstract deleteQcDocument(documentId: string): Observable<void>;
  abstract getAssetUseTypes(contractId?: number | null): Observable<AssetUseType[]>;
  abstract getAssetRequiredFields(contractId?: number | null): Observable<AssetRequiredField[]>;
  abstract deliverQualityControl(qualityControlId: string): Observable<void>;
  abstract getKoOptions(contractId?: number | null): Observable<KoOption[]>;
  abstract getQualityControlOrigins(): Observable<QualityControlOriginOption[]>;
  abstract getInterfaceDatas(qualityControlId: string, contractId?: number | null): Observable<QualityControlInterfaceData>;
  abstract getCommunicationHistory(qualityControlId: string): Observable<QcCommunicationHistoryItem[]>;
}
